const PV_SELECTOR = '[data-pv-landing]';

class ParadiseViceLanding {
  constructor(root) {
    this.root = root;
    this.variants = JSON.parse(root.querySelector('[data-pv-variants]')?.textContent || '[]');
    this.currency = root.dataset.currency || 'USD';
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency,
      maximumFractionDigits: 2,
    });
    this.form = root.querySelector('[data-pv-form]');
    this.variantInput = root.querySelector('[data-pv-variant-id]');
    this.addButton = root.querySelector('[data-pv-add]');
    this.releaseLink = root.querySelector('[data-pv-release]');
    this.slides = root.querySelector('[data-pv-slides]');
    this.sticky = root.querySelector('[data-pv-sticky]');
    this.mainAction = root.querySelector('[data-pv-main-action]');
    this.mainActionVisible = true;
    this.bind();
    this.update();
  }

  bind() {
    this.root.querySelectorAll('[data-pv-option]').forEach((input) => {
      input.addEventListener('change', () => this.update());
    });

    this.root.querySelectorAll('[data-pv-media]').forEach((button) => {
      button.addEventListener('click', () => this.selectMedia(button));
    });

    this.root.querySelector('[data-pv-media-previous]')?.addEventListener('click', () => this.stepMedia(-1));
    this.root.querySelector('[data-pv-media-next]')?.addEventListener('click', () => this.stepMedia(1));

    if (this.slides) {
      this.slides.addEventListener(
        'scroll',
        () => {
          if (this.mediaFrame) window.cancelAnimationFrame(this.mediaFrame);
          this.mediaFrame = window.requestAnimationFrame(() => this.syncMediaFromScroll());
        },
        { passive: true },
      );
      this.slides.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.stepMedia(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.stepMedia(1);
        }
      });
    }

    document.querySelectorAll('[data-pv-proxy-action]').forEach((button) => {
      button.addEventListener('click', () => this.proxyAction());
    });

    if (this.mainAction && this.sticky && 'IntersectionObserver' in window) {
      this.actionObserver = new IntersectionObserver(
        ([entry]) => {
          this.mainActionVisible = entry.isIntersecting;
          this.sticky.hidden = entry.isIntersecting || this.shouldHideSticky();
        },
        { threshold: 0.55 },
      );
      this.actionObserver.observe(this.mainAction);
    }

    document.addEventListener('focusin', (event) => {
      if (this.sticky && ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) {
        this.sticky.hidden = true;
      }
    });

    document.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (this.sticky && !this.mainActionVisible) {
          this.sticky.hidden = this.shouldHideSticky();
        }
      }, 160);
    });

    new MutationObserver(() => {
      if (this.sticky && this.shouldHideSticky()) this.sticky.hidden = true;
    }).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open', 'class'] });
  }

  selections() {
    return [...this.root.querySelectorAll('[data-pv-option]:checked')].map((input) => input.value);
  }

  selectedVariant() {
    const selected = this.selections();
    return this.variants.find(
      (variant) =>
        variant.options.length === selected.length &&
        variant.options.every((option, index) => option === selected[index]),
    );
  }

  update() {
    const variant = this.selectedVariant();
    if (!variant) return;

    this.variantInput.value = variant.id;
    this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateOptionLabels();
    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateInventory(variant);
    this.updateMedia(variant);
    this.updateSticky(variant);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  updateOptionLabels() {
    this.root.querySelectorAll('[data-pv-option-group]').forEach((group) => {
      const checked = group.querySelector('[data-pv-option]:checked');
      const output = group.querySelector('[data-pv-option-output]');
      if (checked && output) output.textContent = checked.value;
    });
  }

  updatePrice(variant) {
    const price = this.root.querySelector('[data-pv-price]');
    const compare = this.root.querySelector('[data-pv-compare]');
    if (price) price.textContent = this.money.format(variant.price / 100);
    if (compare) {
      compare.textContent = variant.compare_at_price ? this.money.format(variant.compare_at_price / 100) : '';
      compare.hidden = !variant.compare_at_price || variant.compare_at_price <= variant.price;
    }
  }

  updateAvailability(variant) {
    if (!this.addButton || !this.releaseLink) return;
    this.addButton.disabled = !variant.available;
    this.addButton.hidden = !variant.available;
    this.releaseLink.hidden = variant.available;

    const payment = this.root.querySelector('[data-pv-payment]');
    if (payment) payment.hidden = !variant.available;
  }

  updateInventory(variant) {
    const status = this.root.querySelector('[data-pv-inventory]');
    if (!status) return;

    const threshold = Number(this.root.dataset.inventoryThreshold || 10);
    const optionNames = this.root.dataset.optionNames?.split('|') || [];
    const sizeIndex = optionNames.findIndex((name) => name.toLowerCase() === 'size');
    const size = sizeIndex >= 0 ? variant.options[sizeIndex] : '';
    const quantity = Number(variant.inventory_quantity);
    let message = '';
    let tone = '';

    if (!variant.available) {
      message = size ? `${size} is currently sold out` : 'Currently unavailable — join the release list';
      tone = 'urgent';
    } else if (variant.inventory_management && variant.inventory_policy !== 'continue' && quantity <= threshold) {
      if (quantity === 1) message = `Only 1 left${size ? ` in ${size}` : ''}`;
      else if (quantity >= 2 && quantity <= 5) message = `Only ${quantity} left${size ? ` in ${size}` : ''}`;
      else if (quantity >= 6) message = `Low stock${size ? ` in ${size}` : ''}`;
      tone = 'urgent';
    }

    status.textContent = message;
    status.dataset.tone = tone;
  }

  updateMedia(variant) {
    if (!variant.featured_media_id) return;
    const button = this.root.querySelector(`[data-pv-media="${variant.featured_media_id}"]`);
    if (button) this.selectMedia(button);
  }

  selectMedia(button) {
    if (!this.slides) return;
    const slide = this.root.querySelector(`[data-pv-slide="${button.dataset.pvMedia}"]`);
    if (!slide) return;
    this.slides.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this.setActiveMedia(button, slide.dataset.position);
  }

  setActiveMedia(button, position) {
    this.root.querySelectorAll('[data-pv-media]').forEach((item) => {
      item.setAttribute('aria-current', String(item === button));
    });
    button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    const counter = this.root.querySelector('[data-pv-media-counter]');
    if (counter) counter.textContent = position || button.dataset.position;
  }

  syncMediaFromScroll() {
    if (!this.slides) return;
    const slideItems = [...this.root.querySelectorAll('[data-pv-slide]')];
    if (!slideItems.length) return;
    const closest = slideItems.reduce((current, slide) => {
      const currentDistance = Math.abs(current.offsetLeft - this.slides.scrollLeft);
      const nextDistance = Math.abs(slide.offsetLeft - this.slides.scrollLeft);
      return nextDistance < currentDistance ? slide : current;
    });
    const button = this.root.querySelector(`[data-pv-media="${closest.dataset.pvSlide}"]`);
    if (button) this.setActiveMedia(button, closest.dataset.position);
  }

  stepMedia(direction) {
    if (!this.slides) return;
    const slideItems = [...this.root.querySelectorAll('[data-pv-slide]')];
    if (!slideItems.length) return;
    const activeButton = this.root.querySelector('[data-pv-media][aria-current="true"]');
    const currentIndex = slideItems.findIndex((slide) => slide.dataset.pvSlide === activeButton?.dataset.pvMedia);
    const nextIndex = Math.min(Math.max(currentIndex + direction, 0), slideItems.length - 1);
    const nextButton = this.root.querySelector(`[data-pv-media="${slideItems[nextIndex].dataset.pvSlide}"]`);
    if (nextButton) this.selectMedia(nextButton);
  }

  updateSticky(variant) {
    const selection = this.root.querySelector('[data-pv-sticky-selection]');
    const price = this.root.querySelector('[data-pv-sticky-price]');
    const label = this.root.querySelector('[data-pv-sticky-label]');
    if (selection) selection.textContent = variant.options.join(' / ');
    if (price) price.textContent = this.money.format(variant.price / 100);
    if (label) label.textContent = variant.available ? label.dataset.available : label.dataset.unavailable;
  }

  proxyAction() {
    const variant = this.selectedVariant();
    if (variant?.available && this.addButton) {
      this.addButton.click();
      return;
    }
    const target = document.querySelector(this.root.dataset.releaseTarget || '#pv-release-list');
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  shouldHideSticky() {
    return Boolean(
      document.querySelector('dialog[open], cart-drawer[open], .cart-drawer[open], [data-cart-drawer][open]'),
    );
  }
}

document.querySelectorAll(PV_SELECTOR).forEach((root) => {
  if (!root.dataset.pvInitialized) {
    root.dataset.pvInitialized = 'true';
    new ParadiseViceLanding(root);
  }
});
